import React from 'react';
import {
  ICON_ACCOUNT_OUTLINE,
  ICON_COG_OUTLINE,
  ICON_BULLHORN_OUTLINE,
  ICON_PLUS,
  ICON_FORMAT_LIST_BULLETED_SQUARE,
  ICON_MESSAGE_PROCESSING_OUTLINE,
  ICON_MAGNIFY,
  ICON_HOME_PLUS_OUTLINE,
  ICON_LINK,
  ICON_FILE_DOCUMENT_OUTLINE,
  ICON_ACCOUNT_MULTIPLE_OUTLINE,
  ICON_PHONE_OUTLINE,
  ICON_VIEW_GRID_OUTLINE,
  ICON_HEART_OUTLINE,
  ICON_ARROW_UP,
  ICON_DOMAIN,
  ICON_MAP_CHECK_OUTLINE,
  ICON_FILE_DOCUMENT_MULTIPLE_OUTLINE,
  ICON_ACCOUNT_COG_OUTLINE,
  ICON_FILE_DOCUMENT_ARROW_RIGHT_OUTLINE,
  ICON_FILE_SIGN,
  ICON_HANDSHAKE_OUTLINE,
  ICON_SCHOOL_OUTLINE,
  ICON_ACCOUNT_SEARCH_OUTLINE,
  ICON_MAP_SEARCH_OUTLINE,
  ICON_LIST_BOX_OUTLINE,
  ICON_CASH_CHECK,
  ICON_IMAGE_OUTLINE,
  ICON_IMAGE_EDIT_OUTLINE,
  ICON_IMAGE_SEARCH_OUTLINE,
  ICON_IMAGE_AUTO_ADJUST,
} from '@doveit/bricks';
import { NavigationItem } from '../../types';
import useRBAC from '../use-rbac/useRBAC';
import { useCurrentAgentSync } from '../use-agent/useAgent';
import { isTrialAgent } from '../../utils/agent/isTrialAgent';
import { ResourceSection } from '../../resources/types';
import { JobTitle } from '../../domain/types';
import { removeNullAndUndefinedValues } from '../../utils/array';
import useUserPermissions from '../use-user-permissions/useUserPermissions';

export function findNavigationItem(data: NavigationItem[], pathname: string): NavigationItem | undefined {
  if (data.length === 0) return undefined;

  return data.find((item) => item.path && item.path === pathname)
    || findNavigationItem(data.flatMap((item) => item.items || []), pathname)
    || undefined;
}

export default function useMainNavigation(): NavigationItem[] {
  const {
    userIsAdmin,
    userIsAgent,
    userIsCallCenter,
    userIsContentEditor,
    userIsContentWriter,
    userIsFinance,
    userIsHR,
    userIsAreaManager,
    userIsMarketing,
  } = useRBAC();

  const { data: currentAgent } = useCurrentAgentSync();
  const userPermissions = useUserPermissions(['contacts', 'rumors', 'jobApplications']);
  const franchisingAgency = React.useMemo(
    () => (currentAgent?.franchising ? currentAgent.agencies?.find((agency) => agency.franchising) : undefined),
    [currentAgent],
  );

  const userIsBroker = React.useMemo(
    () => currentAgent?.jobTitle === JobTitle.BROKER,
    [currentAgent],
  );

  if (userIsContentWriter) {
    return [];
  }

  const navigation: NavigationItem[] = [{
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: ICON_VIEW_GRID_OUTLINE,
  }];

  const isATrialAgent = isTrialAgent(currentAgent);
  const userIsActiveAreaManagerOrActiveBroker = (userIsAreaManager || userIsBroker) && (currentAgent && !currentAgent.contract.endDate);
  const showContactMenu = !!userPermissions.contacts.canSearch;
  const showCallCenterMenu = userIsAdmin || userIsCallCenter;
  const showProspectsMenu = userIsAdmin || userIsAgent;
  const showRumorMenu = userIsAdmin || (userIsAgent && !isATrialAgent);
  const showGeographyMenu = userIsAdmin || userIsContentEditor || userIsCallCenter || userIsHR;
  const showMarketingMenu = userIsAdmin || (userIsAgent && !isATrialAgent) || userIsContentEditor || userIsMarketing;
  const showAdministrationMenu = userIsAdmin || userIsFinance || userIsCallCenter || userIsHR || userIsContentEditor || userIsActiveAreaManagerOrActiveBroker || userIsMarketing;
  const enableResourcesMenuItem = userIsAdmin || userIsContentEditor || userIsAgent || userIsHR;
  const canAccessCanva = userIsMarketing || !!currentAgent?.configuration?.marketing?.isCanvaAccessEnabled;

  if (showCallCenterMenu) {
    navigation.push({
      id: 'call-center',
      label: 'Call Center',
      title: 'Call center',
      icon: ICON_PHONE_OUTLINE,
      items: [
        {
          id: 'call-center-add',
          path: '/call-center/contacts/create',
          label: 'Aggiungi lead',
          icon: ICON_PLUS,
        },
        {
          id: 'call-center-process',
          path: '/call-center/contacts/process',
          label: 'Gestisci chiamate',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
        },
        {
          id: 'call-center-messaging',
          path: '/call-center/messaging',
          label: 'Centro messaggi',
          icon: ICON_MESSAGE_PROCESSING_OUTLINE, // @TODO: Sostiture icon dopo avere aggiunto su Bricks
        },
      ],
    });
  }

  if (showContactMenu) {
    navigation.push({
      id: 'contacts',
      label: 'Contatti',
      icon: ICON_ACCOUNT_OUTLINE,
      items: [
        {
          id: 'contacts-search',
          path: '/contacts/search',
          label: 'Cerca contatto',
          icon: ICON_MAGNIFY,
        },
      ],
    });
  }

  if (showProspectsMenu) {
    const prospectMenu: NavigationItem = {
      id: 'handle-prospects',
      label: 'Acquisizione',
      icon: ICON_ARROW_UP,
      items: [{
        id: 'handle-prospects',
        path: '/prospects/explore',
        label: 'Gestisci valutazioni',
        icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
      }],
    };

    if (showRumorMenu) {
      prospectMenu.items = removeNullAndUndefinedValues([
        userPermissions.rumors.canAdd ? {
          id: 'rumor-add',
          path: '/rumors/create',
          label: 'Aggiungi notizia',
          icon: ICON_PLUS,
        } : undefined,
        {
          id: 'handle-rumors',
          path: '/rumors/explore',
          label: 'Gestisci notizie',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
        },
        ...prospectMenu.items!,
      ]);
    }

    navigation.push(prospectMenu);
  }

  const propertyItems: NavigationItem = {
    id: 'properties-all',
    label: 'Immobili',
    icon: ICON_DOMAIN,
    items: [
      {
        id: 'handle-properties',
        path: '/properties/explore',
        label: 'Gestisci immobili',
        icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
      },
    ],
  };

  navigation.push(propertyItems);

  navigation.push(
    {
      id: 'intents-all',
      label: 'Interessi',
      icon: ICON_HEART_OUTLINE,
      items: [
        {
          id: 'intents-add',
          path: '/intents/create',
          label: 'Aggiungi interesse',
          icon: ICON_PLUS,
        },
        {
          id: 'intents-process',
          path: '/intents/explore',
          label: 'Gestisci interessi',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
        },
        {
          id: 'search-criteria-add',
          path: '/search-criteria/create',
          label: 'Aggiungi ricerca',
          icon: ICON_HOME_PLUS_OUTLINE,
        },
      ],
    },
  );

  if (userIsAdmin || userIsContentEditor) {
    const taskItems: NavigationItem = {
      id: 'tasks-all',
      label: 'Attività',
      icon: ICON_LIST_BOX_OUTLINE,
      items: [
        {
          id: 'handle-tasks',
          path: '/tasks/explore',
          label: 'Gestisci attività',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
        },
      ],
    };

    navigation.push(taskItems);
  }

  if (showGeographyMenu) {
    navigation.push(
      {
        id: 'areas-search',
        label: 'Geografie',
        icon: ICON_MAP_CHECK_OUTLINE,
        items: [
          {
            id: 'search-agent-by-cap',
            label: 'Cerca CAP',
            icon: ICON_MAGNIFY,
            path: '/agent/search/zipcode',
          },
          {
            id: 'search-agent-by-locality',
            label: 'Cerca città',
            icon: ICON_MAP_SEARCH_OUTLINE,
            path: '/agent/search/locality',
          },
          {
            id: 'search-agent-by-areas',
            label: 'Cerca agente',
            icon: ICON_ACCOUNT_SEARCH_OUTLINE,
            path: '/agent/search/areas',
          },
        ],
      },
    );
  }

  if (showMarketingMenu) {
    const marketingItems: NavigationItem[] = [
      {
        id: 'tracked-links-create',
        path: '/marketing',
        label: 'Crea link tracciato',
        icon: ICON_LINK,
      },
      {
        id: 'catalogue',
        path: '/marketing/catalogues',
        label: 'Materiali promozionali',
        icon: ICON_FILE_DOCUMENT_OUTLINE,
      },
      {
        id: 'generate-materials',
        path: '/marketing/generate',
        label: 'Genera materiali',
        icon: ICON_FILE_DOCUMENT_ARROW_RIGHT_OUTLINE,
      },
    ];

    navigation.push(
      {
        id: 'marketing',
        label: 'Marketing',
        icon: ICON_BULLHORN_OUTLINE,
        items: marketingItems,
      },
    );
  }

  if (canAccessCanva) {
    navigation.push({
      id: 'canva-material',
      label: 'Materiali Canva',
      icon: ICON_IMAGE_AUTO_ADJUST,
      items: [
        {
          id: 'canva-explore',
          path: '/marketing/canva/explore',
          label: 'Esplora risorse',
          icon: ICON_IMAGE_SEARCH_OUTLINE,
        },
        {
          id: 'canva-templates-list',
          path: '/marketing/canva/brand-templates',
          label: 'Lista template',
          icon: ICON_IMAGE_EDIT_OUTLINE,
        }, {
          id: 'canva-designs-list',
          path: '/marketing/canva/designs',
          label: 'I tuoi design',
          icon: ICON_IMAGE_OUTLINE,
        },
      ],
    });
  }

  const adminItems: NavigationItem[] = [];

  if (userIsAdmin || userIsCallCenter || userIsHR || userIsContentEditor || userIsFinance || userIsActiveAreaManagerOrActiveBroker || userIsMarketing) {
    adminItems.push({
      id: 'agent-manage',
      path: '/agents/explore',
      label: 'Gestisci agenti',
      icon: ICON_ACCOUNT_COG_OUTLINE,
    });
  }

  if (userIsAdmin || userIsFinance) {
    adminItems.push({
      id: 'invoices-explore',
      path: '/invoices/explore',
      label: 'Gestisci pagamenti',
      icon: ICON_CASH_CHECK,
    });
  }

  if (userIsAdmin) {
    adminItems.push({
      id: 'create-agency',
      path: '/agencies/create',
      label: 'Aggiungi agenzia',
      icon: ICON_HOME_PLUS_OUTLINE,
    });
  }

  if (showAdministrationMenu) {
    navigation.push(
      {
        id: 'administration',
        label: 'Amministrazione',
        icon: ICON_COG_OUTLINE,
        items: adminItems,
      },
    );
  }

  if (enableResourcesMenuItem) {
    navigation.push(
      {
        id: 'resources',
        label: 'Risorse',
        icon: ICON_FILE_DOCUMENT_MULTIPLE_OUTLINE,
        items: isATrialAgent
          ? [
            {
              id: 'documentation',
              label: 'Documentazione',
              path: franchisingAgency ? `/resources/${franchisingAgency.id}/${ResourceSection.DOCUMENTATION}` : `/resources/${ResourceSection.DOCUMENTATION}`,
              icon: ICON_FILE_SIGN,
            },
          ]
          : [{
            id: 'documentation',
            label: 'Documentazione',
            path: franchisingAgency ? `/resources/${franchisingAgency.id}/${ResourceSection.DOCUMENTATION}` : `/resources/${ResourceSection.DOCUMENTATION}`,
            icon: ICON_FILE_SIGN,
          },
          {
            id: 'partners',
            label: 'Partner',
            path: franchisingAgency ? `/resources/${franchisingAgency.id}/${ResourceSection.PARTNERS}` : `/resources/${ResourceSection.PARTNERS}`,
            icon: ICON_HANDSHAKE_OUTLINE,
          },
          {
            id: 'training',
            label: 'Formazione',
            path: franchisingAgency ? `/resources/${franchisingAgency.id}/${ResourceSection.TRAINING}` : `/resources/${ResourceSection.TRAINING}`,
            icon: ICON_SCHOOL_OUTLINE,
          }],
      },
    );
  }

  if (userPermissions.jobApplications.canAdd || userPermissions.jobApplications.canExploreAll) {
    const hrMenu: NavigationItem = {
      id: 'hr',
      label: 'HR',
      icon: ICON_ACCOUNT_MULTIPLE_OUTLINE,
      items: removeNullAndUndefinedValues([
        userPermissions.jobApplications.canAdd ? {
          id: 'job-application-add',
          path: '/job-applications/create',
          label: 'Aggiungi candidatura',
          icon: ICON_PLUS,
        } : undefined,
        userPermissions.jobApplications.canExploreAll ? {
          id: 'job-application-manage',
          path: '/job-applications/explore',
          label: 'Gestisci candidature',
          icon: ICON_FORMAT_LIST_BULLETED_SQUARE,
        } : undefined,
      ]),
    };

    navigation.push(hrMenu);
  }

  return navigation;
}
